import type { BalloonType, DifficultyId } from "./types";

export const WORLD_WIDTH = 1280;
export const WORLD_HEIGHT = 720;

/** Top of the sand on the left, control point, and right shoreline. */
export const BEACH_LEFT_Y = 502;
export const BEACH_CTRL_X = WORLD_WIDTH * 0.45;
export const BEACH_CTRL_Y = 538;
export const BEACH_RIGHT_Y = 624;
/** How far inland (down the screen) the feet stand from the shoreline. */
export const STANDING_INSET = 58;

/** Local Y of the soles in drawCharacter (leg end + round stroke). */
export const PLAYER_FOOT_OFFSET = 53;
export const PLAYER_X = 118;

export function beachGroundY(x: number): number {
  const clamped = Math.min(WORLD_WIDTH, Math.max(0, x));
  const t = beachTForX(clamped);
  const mt = 1 - t;
  return mt * mt * BEACH_LEFT_Y + 2 * mt * t * BEACH_CTRL_Y + t * t * BEACH_RIGHT_Y;
}

export function standingGroundY(x: number): number {
  return beachGroundY(x) + STANDING_INSET;
}

export function playerOriginY(footX: number): number {
  return standingGroundY(footX) - PLAYER_FOOT_OFFSET;
}

function beachTForX(x: number): number {
  const a = WORLD_WIDTH - 2 * BEACH_CTRL_X;
  const b = 2 * BEACH_CTRL_X;
  if (Math.abs(a) < 1e-6) return Math.min(1, Math.max(0, x / b));
  const disc = Math.max(0, b * b - 4 * a * -x);
  const t = (-b + Math.sqrt(disc)) / (2 * a);
  return Math.min(1, Math.max(0, t));
}

export const FRISBEE_MAX = 3;
export const FRISBEE_SPEED = 820;
export const FRISBEE_RADIUS = 18;
export const POWER_DURATION_MS = 10_000;
export const SCORE_TIER = 1_000;
export const MAX_SCORE_FOR_SCALING = 10_000;
export const COMBO_CAP = 10;
export const GOLD_SPAWN_CHANCE = 0.1;
export const COUNTDOWN_SECONDS = 3;

export const BALLOON_SCORES: Record<BalloonType, number> = {
  blue: 100,
  green: 200,
  red: 300,
  gold: 500,
};

export const BALLOON_RADIUS: Record<BalloonType, number> = {
  blue: 46,
  green: 36,
  red: 32,
  gold: 26,
};

/** Rise height in pixels; clamped so balloons stay below the HUD. */
export const BALLOON_PEAK: Record<BalloonType, { min: number; max: number }> = {
  blue: { min: 360, max: 455 },
  green: { min: 380, max: 475 },
  red: { min: 400, max: 490 },
  gold: { min: 370, max: 470 },
};

export const BALLOON_TOP_MARGIN = 82;

export const BALLOON_DURATION: Record<BalloonType, { start: number; end: number }> = {
  blue: { start: 4.6, end: 2.8 },
  green: { start: 3.6, end: 2.2 },
  red: { start: 2.7, end: 1.65 },
  gold: { start: 3.2, end: 1.9 },
};

export const SPAWN_INTERVAL = { start: 2.15, end: 0.85 };

export const DIFFICULTY: Record<
  DifficultyId,
  { lives: number; maxBalloons: number; aimLength: number }
> = {
  easy: { lives: 10, maxBalloons: 1, aimLength: 540 },
  normal: { lives: 5, maxBalloons: 2, aimLength: 340 },
  hard: { lives: 3, maxBalloons: 3, aimLength: 190 },
};

export const SKIN_TONES = ["#f6d4b6", "#e8b98a", "#c98a58", "#a56b3c", "#7a4a2b", "#4f2f1c"];
export const HAIR_COLORS = ["#2b1b12", "#5a3218", "#c47a22", "#e8d27a", "#3b6fb6", "#d4537a", "#1f1f1f", "#f4f1ea"];
export const OUTFIT_COLORS = ["#ff6b8a", "#3db8ff", "#7ad36a", "#ffb347", "#9b7bff", "#ff5c5c", "#2ec4b6", "#f4f1ea"];

export const DAY_CYCLE_MS = 90_000;
export const THROW_POSE_MS = 220;

export const STORAGE_KEY = "beach-pop-save-v1";
